import { useCallback, useEffect, useState } from 'react'
import {
  useWbsItemSelectColumnOptions,
  WbsItemSelectColumnOptionType,
} from '../../../hooks/useWbsItemSelectColumnOptions'
import { parse } from '../../../../lib/commons/i18nLabel'
import {
  CustomEnumCode,
  getByCodeAndGroupKeys,
} from '../../../../lib/functions/customEnumValue'
import ticketListApi from '../../../../lib/functions/ticketList'

export const useSelectColumnOptions = (
  projectUuid: string,
  ticketType: string | undefined
) => {
  const [selectColumnOptions, setSelectColumnOptions] = useState<
    WbsItemSelectColumnOptionType | undefined
  >(undefined)
  const {
    fetchedSelectColumnOptions: fetchedWbsItemSelectColumnOptions,
    selectColumnOptions: wbsItemSelectColumnOptions,
  } = useWbsItemSelectColumnOptions({
    projectUuid,
    targetColumnOptionTypes: [
      'STATUS',
      'PRIORITY',
      'DIFFICULTY',
      'TEAM',
      'MEMBER',
      'SPRINT',
      'TAG',
    ],
  })

  const fetchSubstatusOptions = useCallback(async groupKeys => {
    const response = await getByCodeAndGroupKeys({
      customEnumCode: CustomEnumCode.WBS_SUBSTATUS,
      groupKeys,
    })
    return response.json.map(v => ({ ...v, nameI18n: parse(v.nameI18n) }))
  }, [])

  const fetchTicketListOptions = useCallback(
    async () => ticketListApi.searchAll(),
    []
  )

  const fetchSelectColumnOptions = useCallback(
    async (projectUuid: string) => {
      const ticketList = await fetchTicketListOptions()
      setSelectColumnOptions(prevState => ({
        ...(prevState || {}),
        ticketList,
      }))
    },
    [fetchTicketListOptions]
  )

  const fetchSelectColumnOptionsByTicketTypes = useCallback(
    async (projectUuid: string, ticketType: string | undefined) => {
      if (!ticketType) return
      const substatus = await fetchSubstatusOptions([projectUuid, ticketType])
      setSelectColumnOptions(prevState => ({
        ...(prevState || {}),
        substatus,
      }))
    },
    [fetchSubstatusOptions]
  )

  useEffect(() => {
    if (!fetchedWbsItemSelectColumnOptions) return
    setSelectColumnOptions(prevState => ({
      ...(prevState || {}),
      status: wbsItemSelectColumnOptions
        ? wbsItemSelectColumnOptions['status']
        : [],
      priority: wbsItemSelectColumnOptions
        ? wbsItemSelectColumnOptions['priority']
        : [],
      difficulty: wbsItemSelectColumnOptions
        ? wbsItemSelectColumnOptions['difficulty']
        : [],
      team: wbsItemSelectColumnOptions
        ? wbsItemSelectColumnOptions['team']
        : [],
      member: wbsItemSelectColumnOptions
        ? wbsItemSelectColumnOptions['member']
        : [],
      sprint: wbsItemSelectColumnOptions
        ? wbsItemSelectColumnOptions['sprint']
        : [],
      tag: wbsItemSelectColumnOptions ? wbsItemSelectColumnOptions['tag'] : [],
    }))
  }, [fetchedWbsItemSelectColumnOptions, wbsItemSelectColumnOptions])

  useEffect(() => {
    fetchSelectColumnOptions(projectUuid)
  }, [projectUuid, fetchSelectColumnOptions])

  useEffect(() => {
    fetchSelectColumnOptionsByTicketTypes(projectUuid, ticketType)
  }, [projectUuid, ticketType, fetchSelectColumnOptionsByTicketTypes])

  const clearSelectColumnOptionsRelatedToTicketType = useCallback(
    () =>
      setSelectColumnOptions(prevState => ({
        ...(prevState || {}),
        substatus: [],
      })),
    []
  )

  return {
    selectColumnOptions,
    clearSelectColumnOptionsRelatedToTicketType,
  }
}
